import type { NamespaceKey } from '@giyus/i18n';

import { entryVisible, type NavItem } from './navItems';
import type { Permission } from '../lib/permissions';
import { buildModuleSubmenus, type SubmenuItem } from './sidebarSubmenus';

export interface SidebarGroup extends NavItem {
  readonly children: readonly SubmenuItem[];
  readonly roots: readonly string[];
}

const SUBMENU_PERMISSIONS: Readonly<Record<string, Permission | readonly Permission[]>> = {
  'authz-catalog': 'authz.read',
  'authz-matrix': 'authz.read',
  'authz-assignments': 'authz.assignment.read',
  'authz-sod': 'authz.read',
  'authz-breakglass': 'breakglass.review',
  'authz-reviews': 'access.review.read',
  'authz-pit': 'authz.history.read',
  'authz-audit': 'audit.read.network',
  'authz-tenant-assignments': 'authz.assignment.read.tenant',
  'authz-tenant-delegations': ['delegation.read', 'delegation.manage'],
  'authz-tenant-breakglass': 'breakglass.use',
  'authz-tenant-audit': 'audit.read',
};

/** Presentation groups retain the routes and visibility already resolved by buildNavItems. */
export function buildSidebarGroups(
  items: readonly NavItem[],
  network: boolean,
  permissions: ReadonlySet<string>,
): SidebarGroup[] {
  const groups = items.map((item): SidebarGroup => ({
    ...item,
    children: buildModuleSubmenus(item.key, item.to, network).filter((child) =>
      entryVisible(SUBMENU_PERMISSIONS[child.id] ?? null, permissions),
    ),
    roots: [item.to],
  }));
  const people = groups.find((item) => item.key === 'people');
  if (people && network) {
    groups[groups.indexOf(people)] = {
      ...people,
      children: people.children.filter((c) => c.id !== 'people-research-centers'),
    };
  }
  const identity = groups.find((item) => item.key === 'identity');
  if (identity) {
    groups[groups.indexOf(identity)] = { ...identity, labelKey: 'nav.portal.users' };
  }

  const studies = groups.find((item) => item.key === 'studies');
  if (studies) {
    const proceduresBase = studies.to.replace(/\/studies$/, '/procedures');
    const reimbursementsBase = studies.to.replace(/\/studies$/, '/participant-reimbursements');
    groups.push({
      key: 'procedures',
      to: proceduresBase,
      labelKey: 'nav.portal.procedures',
      roots: [proceduresBase],
      children: [
        {
          id: 'procedures-catalog',
          to: `${proceduresBase}?tab=catalog`,
          labelKey: 'nav.submenus.procedures-catalog',
        },
        {
          id: 'procedures-composite',
          to: `${proceduresBase}?tab=composite`,
          labelKey: 'nav.submenus.procedures-composite',
        },
        {
          id: 'procedures-bom',
          to: `${proceduresBase}?tab=bom`,
          labelKey: 'nav.submenus.procedures-bom',
        },
      ],
    });
    groups.push({
      key: 'participant-reimbursements',
      to: reimbursementsBase,
      labelKey: 'nav.portal.participantReimbursements',
      roots: [reimbursementsBase],
      children: [
        {
          id: 'reimb-launch-rules',
          to: `${reimbursementsBase}?tab=launch_and_rules`,
          labelKey: 'nav.submenus.reimb-launch-rules',
        },
        {
          id: 'reimb-uber-ifood',
          to: `${reimbursementsBase}?tab=uber_and_ifood_hub`,
          labelKey: 'nav.submenus.reimb-uber-ifood',
        },
        {
          id: 'reimb-visit-forecast',
          to: `${reimbursementsBase}?tab=visit_forecast`,
          labelKey: 'nav.submenus.reimb-visit-forecast',
        },
        {
          id: 'reimb-settlement',
          to: `${reimbursementsBase}?tab=supplier_and_contract_settlement`,
          labelKey: 'nav.submenus.reimb-settlement',
        },
        {
          id: 'reimb-caps-matrix',
          to: `${reimbursementsBase}?tab=contract_caps_matrix`,
          labelKey: 'nav.submenus.reimb-caps-matrix',
        },
      ],
    });

    if (network) {
      groups.push({
        key: 'research-centers',
        to: '/network/people/research-centers',
        labelKey: 'nav.portal.researchCenters',
        roots: ['/network/people/research-centers', '/network/research-centers'],
        children: [
          {
            id: 'research-centers-directory',
            to: '/network/people/research-centers',
            labelKey: 'nav.submenus.research-centers-directory',
          },
          {
            id: 'research-centers-studies',
            to: '/network/people/research-centers/details',
            labelKey: 'nav.submenus.research-centers-studies',
          },
        ],
      });
    }
  }

  const suppliers = groups.find((item) => item.key === 'suppliers');
  if (suppliers && network) {
    const financeIds = new Set([
      'suppliers-timeline',
      'suppliers-pendencies',
      'suppliers-financial',
    ]);
    const financeChildren = suppliers.children.filter((child) => financeIds.has(child.id));
    groups[groups.indexOf(suppliers)] = {
      ...suppliers,
      children: [
        ...suppliers.children.filter((child) => !financeIds.has(child.id)),
        {
          id: 'procurement',
          to: `${suppliers.to}?tab=procurement`,
          labelKey: 'nav.portal.procurement',
        },
      ],
    };
    groups.push({
      key: 'finance',
      to: `${suppliers.to}?tab=financial`,
      labelKey: 'nav.portal.finance',
      roots: [
        `${suppliers.to}?tab=financial`,
        `${suppliers.to}?tab=timeline`,
        `${suppliers.to}?tab=pendencies`,
        `${suppliers.to}?tab=billableRework`,
        `${suppliers.to}?tab=cashflowForecast`,
      ],
      children: financeChildren,
    });
    groups.push({
      key: 'accounting',
      to: '/network/accounting',
      labelKey: 'nav.portal.accounting',
      roots: ['/network/accounting', `${suppliers.to}?tab=accounting`],
      children: [
        {
          id: 'accounting-basket-wip',
          to: '/network/accounting?tab=basket_wip',
          labelKey: 'nav.submenus.accounting-basket-wip',
        },
        {
          id: 'accounting-outbound-nf',
          to: '/network/accounting?tab=outbound_invoices',
          labelKey: 'nav.submenus.accounting-outbound-nf',
        },
        {
          id: 'accounting-inbound-nf',
          to: '/network/accounting?tab=inbound_invoices',
          labelKey: 'nav.submenus.accounting-inbound-nf',
        },
        {
          id: 'accounting-tax-engine',
          to: '/network/accounting?tab=tax_engine',
          labelKey: 'nav.submenus.accounting-tax-engine',
        },
        {
          id: 'accounting-dre',
          to: '/network/accounting?tab=dre_cost_centers',
          labelKey: 'nav.submenus.accounting-dre',
        },
        {
          id: 'accounting-closing',
          to: '/network/accounting?tab=closing_accountant',
          labelKey: 'nav.submenus.accounting-closing',
        },
      ],
    });
  }
  const facilities = groups.find((item) => item.key === 'facilities');
  if (facilities) {
    const equipmentIds = new Set([
      'facilities-assets',
      'facilities-calibration',
      'facilities-timeline-docs',
      'facilities-disposition',
      'facilities-suppliers-rbc',
      'facilities-mailbox-reconciliation',
      'facilities-tenant-assets',
      'facilities-tenant-calibration',
      'facilities-tenant-timeline-docs',
      'facilities-tenant-disposition',
    ]);
    const equipmentChildren = facilities.children.filter((child) => equipmentIds.has(child.id));
    groups[groups.indexOf(facilities)] = {
      ...facilities,
      to: `${facilities.to}?tab=stock_alerts`,
      children: facilities.children.filter((child) => !equipmentIds.has(child.id)),
      roots: [
        `${facilities.to}?tab=stock_alerts`,
        `${facilities.to}?tab=shortage_12m`,
        `${facilities.to}?tab=products_catalog`,
      ],
    };
    if (equipmentChildren.length > 0) {
      const equipPath = facilities.to.replace(/\/facilities$/, '/equipment');
      groups.push({
        key: 'equipment',
        to: `${facilities.to}?tab=assets`,
        labelKey: 'nav.portal.equipment',
        roots: [facilities.to, equipPath, `${facilities.to}/assets`],
        children: equipmentChildren,
      });
    }
  }

  const authz = groups.find((item) => item.key === 'authz');
  if (authz) {
    const governanceIds = new Set([
      'authz-sod',
      'authz-reviews',
      'authz-pit',
      'authz-audit',
      'authz-tenant-audit',
    ]);
    const children = authz.children.filter((child) => governanceIds.has(child.id));
    groups[groups.indexOf(authz)] = {
      ...authz,
      labelKey: 'nav.portal.access',
      children: authz.children.filter((child) => !governanceIds.has(child.id)),
    };
    if (authz.children.every((child) => governanceIds.has(child.id))) {
      groups.splice(
        groups.findIndex((group) => group.key === 'authz'),
        1,
      );
    }
    if (children[0])
      groups.push({
        key: 'governance',
        to: children[0].to,
        labelKey: 'nav.portal.governance',
        roots: [authz.to.replace(/\/authz$/, '/governance')],
        children,
      });
  }
  const notifications = groups.find((item) => item.key === 'notifications');
  const settings = groups.find((item) => item.key === 'settings');
  if (notifications && settings) {
    groups.splice(groups.indexOf(notifications), 1);
    groups[groups.indexOf(settings)] = {
      ...settings,
      children: [
        ...settings.children,
        {
          id: 'settings-whatsapp-business',
          to: `${settings.to}?tab=integrations`,
          labelKey: 'nav.portal.whatsapp',
        },
        {
          id: 'settings-notification-templates',
          to: notifications.to,
          labelKey: 'nav.modules.notifications',
        },
      ],
      roots: [...settings.roots, ...notifications.roots],
    };
  }
  const labels: Record<string, NamespaceKey<'shell'>> = {
    studies: 'nav.portal.contracts',
    facilities: 'nav.portal.inventory',
    notifications: 'nav.portal.whatsapp',
    scheduling: 'nav.portal.schedule',
    settings: 'nav.portal.settings',
  };
  const order = [
    'home',
    'shared-mailboxes',
    'research-centers',
    'studies',
    'procedures',
    'participant-reimbursements',
    'facilities',
    'equipment',
    'suppliers',
    'finance',
    'accounting',
    'people',
    'identity',
    'scheduling',
    'notifications',
    'authz',
    'governance',
    'documents',
    'workflow',
    'reports',
    'settings',
    'platform-health',
  ];
  return groups
    .map((group) => ({ ...group, labelKey: labels[group.key] ?? group.labelKey }))
    .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

/** Query-specific destinations must not select every item sharing the same pathname. */
export function destinationMatches(
  to: string,
  pathname: string,
  search: string,
  exact = false,
): boolean {
  const [path, query] = to.split('?');
  if (query) {
    const current = new URLSearchParams(search);
    return (
      path === pathname &&
      [...new URLSearchParams(query)].every(([key, value]) => current.get(key) === value)
    );
  }
  return path === pathname || (!exact && pathname.startsWith(`${path}/`));
}

export function activeSidebarKey(
  groups: readonly SidebarGroup[],
  pathname: string,
  search: string,
): string | undefined {
  const exactChild = groups.find((group) =>
    group.children.some((child) => destinationMatches(child.to, pathname, search, true)),
  );
  if (exactChild) return exactChild.key;

  const exactQueryRoot = groups.find((group) =>
    group.roots.some((root) => root.includes('?') && destinationMatches(root, pathname, search, true)),
  );
  if (exactQueryRoot) return exactQueryRoot.key;

  let bestPrefixKey: string | undefined;
  let bestPrefixLen = -1;
  for (const group of groups) {
    for (const child of group.children) {
      if (destinationMatches(child.to, pathname, search, false)) {
        const pathLen = (child.to.split('?')[0] ?? '').length;
        if (pathLen > bestPrefixLen) {
          bestPrefixLen = pathLen;
          bestPrefixKey = group.key;
        }
      }
    }
  }
  if (bestPrefixKey) return bestPrefixKey;

  return groups.find((group) =>
    group.key === 'home'
      ? group.to === pathname
      : group.roots.some((root) => destinationMatches(root, pathname, search)),
  )?.key;
}
